/**
 * PortraitFrame (FEAT-023) — Momento never turns sideways.
 *
 * Wraps the whole app. Upright it's a plain passthrough. Turned sideways it
 * renders a portrait-shaped box and rotates it back into the landscape
 * viewport — an H×W box turned 90° occupies exactly W×H, so it fills the screen
 * with no letterboxing and nothing on screen appears to move. The shutter
 * circle stays under the same thumb, in the same place.
 *
 * Everything inside is unchanged, including `position: fixed` descendants: a
 * transformed ancestor makes them fixed to THIS box, and this box is the
 * viewport, so they land where they always did.
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { watchOrientation, type FrameOrientation } from '../utils/deviceOrientation';

const UPRIGHT: FrameOrientation = { pinned: false, rotation: 0 };

export default function PortraitFrame({ children }: { children: ReactNode }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [orientation, setOrientation] = useState<FrameOrientation>(UPRIGHT);
  const [host, setHost] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => watchOrientation(setOrientation), []);

  // Measured from the host element, never from vh/vw: viewport units mis-report
  // at iOS standalone cold start (see --viewport-height in index.css), and a
  // frame sized from a wrong number is a black band across the screen.
  useLayoutEffect(() => {
    const node = hostRef.current;
    if (!node) return;
    const measure = () =>
      setHost(prev => {
        const width = node.clientWidth;
        const height = node.clientHeight;
        return prev.width === width && prev.height === height ? prev : { width, height };
      });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const pinned = orientation.pinned && host.width > 0 && host.height > 0;

  // ONE element, always — never `pinned ? <div>{children}</div> : children`.
  // That conditional changes the shape of the tree, so React unmounts and
  // remounts the ENTIRE app on every rotation: a recording in progress dies
  // with its component, and every screen loses its state. Same element, same
  // position, only the class and style change.
  return (
    <div ref={hostRef} className="relative h-full w-full overflow-hidden">
      <div
        className={pinned ? 'portrait-frame' : 'h-full w-full'}
        data-rotation={pinned ? orientation.rotation : undefined}
        style={
          pinned
            ? {
                // Swapped on purpose: the frame is as wide as the screen is tall.
                width: `${host.height}px`,
                height: `${host.width}px`,
                transform: `rotate(${orientation.rotation}deg)`,
              }
            : undefined
        }
      >
        {children}
      </div>
    </div>
  );
}
